import { apiError } from "../../../../../../../utils/formErrors";
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Stack, CircularProgress } from "@mui/material";
import TopBar from "../../../../../../../component/TopBar";
import ActiveBar from "../../../../../../../component/PolicyNavbarUpdated";
import BtnPrimary from "../../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../../component/SecondaryButton";
import SuccessDialog from "../../../../../../../component/SuccessDialog";
import SectionLoader from "../../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../../component/EmptyState";
import { WizardCard, FormSection } from "../../../../../../../component/wizard";
import { getAttendancePolicyById, updateAttendancePolicy, getAllAttendancePolicies } from "../../../../../../../services/attendancePolicy";
import { getOrgDepartments } from "../../../../../../../services/globalData";
import { useNotify } from "../../../../../../../component/NotificationProvider";
import { tokens } from "../../../../../../../theme";

import InlineMessage from "../../../../../../../component/InlineMessage";
const yesNo = (v) => (v ? "Yes" : "No");

// One labelled fact in the summary grid
const Fact = ({ label, value }) => (
  <Box>
    <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Box>
    <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text, mt: "0.25rem" }}>{value ?? "—"}</Box>
  </Box>
);

const Grid = ({ children }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }, gap: "1.25rem" }}>{children}</Box>
);

const AttendancePolicyReview = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [done, setDone] = useState(false);
  const policyId = state?.policyId;

  useEffect(() => {
    if (!policyId) { setLoading(false); return; }
    (async () => {
      try {
        const res = await getAttendancePolicyById(policyId);
        const p = res?.data;
        setPolicy(p);
        // dept-overlap conflict: other ACTIVE policies that target a shared department
        const all = await getAllAttendancePolicies().catch(() => null);
        const deptNames = {};
        (getOrgDepartments() || []).forEach((d) => { deptNames[String(d._id)] = d.name; });
        const myDepts = new Set((p?.department || []).map(String));
        const overlapping = (all?.data || [])
          .filter((o) => String(o._id) !== String(p?._id) && o.status === "active")
          .filter((o) => (o.department || []).some((d) => myDepts.has(String(d))))
          .map((o) => ({
            name: o.name,
            priority: o.priority || 0,
            shared: (o.department || []).filter((d) => myDepts.has(String(d))).map((d) => deptNames[String(d)] || "dept").join(", "),
          }));
        setConflicts(overlapping);
      } catch (e) {
        notify.error(e?.message || "Failed to load policy");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyId]);

  const publish = async () => {
    setPublishing(true);
    try {
      const res = await updateAttendancePolicy(policyId, { status: "active" });
      if (res?.success === false) throw apiError(res, "Could not publish");
      setDone(true);
    } catch (e) {
      notify.error(e?.message || "Could not publish policy");
    } finally {
      setPublishing(false);
    }
  };

  if (!policyId) {
    return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Attendance Policy"} /><EmptyState title="No policy to review" description="Start from the beginning of the wizard." /></Box>;
  }

  const reg = policy?.regularization || {};

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Attendance Policy"}
        breadcrumbs={[{ label: "Attendance", href: "" }, { label: "Policy Details", href: "" }, { label: "Add Category", href: "" }, { label: "Assign Policy", href: "" }, { label: "Approval chain", href: "" }, { label: "Review", href: "" }]} />
      <WizardCard>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
          <ActiveBar active={["Policy Details", "Add Category", "Assign Policy", "Approval chain", "Review"]} />
          <BtnSecondary to="/app/attendance-policy/listing" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>

        {loading ? <SectionLoader /> : !policy ? (
          <EmptyState title="Policy not found" />
        ) : (
          <>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", mb: "1.25rem", px: "0.75rem", py: "0.5rem", borderRadius: "999px", background: tokens.warningTint }}>
              <Box sx={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%", background: tokens.warning }} />
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>Draft — not enforced until published</Box>
            </Box>

            <FormSection title="Policy details" description="">
              <Grid>
                <Fact label="Name" value={policy.name} />
                <Fact label="Code" value={policy.code} />
                <Fact label="Period" value={`${policy.startDate?.split("T")[0]} → ${policy.endDate?.split("T")[0]}`} />
              </Grid>
            </FormSection>

            <FormSection title="Clock-in" description="">
              <Grid>
                <Fact label="Mobile clock-in" value={yesNo(policy.allowMobileClockIn)} />
                <Fact label="Web clock-in" value={yesNo(policy.allowWebClockIn)} />
                <Fact label="Geo-fencing" value={policy.enableGeofencing ? `On (${policy.geofenceMode || "block"}, ±${policy.geofenceToleranceMeters ?? 50}m)` : "Off"} />
                <Fact label="Grace period" value={`${policy.gracePeriodMinutes || 0} min`} />
                <Fact label="Punch rounding" value={policy.punchRoundingMinutes ? `${policy.punchRoundingMinutes} min (${policy.roundingDirection || "nearest"})` : "Off"} />
              </Grid>
            </FormSection>

            <FormSection title="Rules" description="">
              <Grid>
                <Fact label="Tardiness" value={yesNo(policy.enableTardiness)} />
                <Fact label="Absenteeism" value={yesNo(policy.enableAbsenteeism)} />
                <Fact label="Approvers" value={`${(policy.approverIds || []).length} assigned`} />
                <Fact label="Regularization / month" value={reg.maxPerMonth ? reg.maxPerMonth : "Unlimited"} />
                <Fact label="Regularization window" value={reg.windowDays ? `${reg.windowDays} days` : "No limit"} />
                <Fact label="Regularization approval" value={reg.requiresApproval === false ? "Auto-apply" : "Required"} />
                <Fact label="Missing check-out" value={policy.missedCheckoutPolicy || "flag"} />
              </Grid>
            </FormSection>

            <FormSection title="Assignment" description="">
              <Grid>
                <Fact label="Departments" value={`${(policy.department || []).length}`} />
                <Fact label="Employee groups" value={`${(policy.employeeGroup || []).length}`} />
                <Fact label="Shifts" value={`${(policy.shifts || []).length}`} />
                <Fact label="Priority" value={policy.priority || 0} />
              </Grid>
            </FormSection>

            {conflicts.length > 0 && (
              <InlineMessage tone="warning" title="Overlapping policies" sx={{ mt: "0.5rem" }}>
                These active policies also target some of the same departments. The one with the higher priority wins for shared employees (this policy's priority: {policy.priority || 0}).
                {conflicts.map((c, i) => (
                  <Box key={i} sx={{ mt: "0.25rem" }}>
                    <b>{c.name}</b> (priority {c.priority}) — shares: {c.shared}
                  </Box>
                ))}
              </InlineMessage>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "2rem" }}>
              <BtnSecondary to="/app/attendance-policy/listing" sx={{ borderRadius: "8px" }}>Save as draft</BtnSecondary>
              <BtnPrimary onClick={publish} disabled={publishing} sx={{ borderRadius: "8px" }}>
                {publishing ? <CircularProgress size={"1.25rem"} /> : "Publish policy"}
              </BtnPrimary>
            </Stack>
          </>
        )}

        <SuccessDialog
          open={done}
          onClose={() => setDone(false)}
          message={"Policy published successfully!"}
          addNewLink={"/app/attendance-policy/new/setting"}
          addNewText="Create New Policy"
          viewAllLink={"/app/attendance-policy/listing"}
          viewAllText="View All Policies"
        />
      </WizardCard>
    </Box>
  );
};

export default AttendancePolicyReview;
